import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  orderBy,
  updateDoc,
  addDoc,
  onSnapshot,
  deleteDoc,
  runTransaction
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatAge } from '../utils/dateHelpers';
import { onAuthStateChanged } from 'firebase/auth';
import { app, auth, db } from '../firebaseConfig';
import InfoTip from './InfoTip';
import { belongsInSitterTab } from '../utils/applicationLifecycle';

// One-line explanations shown next to each application status pill
const STATUS_TIPS_SITTER = {
  'Pending': 'The parent is reviewing your application.',
  'Awaiting Sitter Confirmation': 'The parent accepted! Confirm within 24 hours or the offer expires.',
  'Active': 'You’re booked — message the family anytime. Submit your work log when the job is done.',
  'Completed': 'Hours submitted — waiting on the parent’s payment (auto-pays after 24 hours). You can still edit your work log.',
  'Paid': 'Money’s on the way to your bank — typically 2–7 business days.',
};

// Plain-English reason a closed application ended up in the "Closed" tab.
const CLOSED_REASON_SITTER = {
  'Family Denied': 'The family chose a different sitter for this job.',
  'Sitter Denied': 'You declined this offer (or it expired before you responded).',
  'Job Filled': 'This job was filled by another caregiver, so your application was closed.',
  'Listing Closed': 'The job’s date passed before it was filled, so the listing closed.',
  'Parent Cancelled': 'The family cancelled this job.',
  'Sitter Cancelled': 'You cancelled this job.',
  'Refunded': 'This job’s payment was refunded and the payout reversed.',
};


const functions = getFunctions(app);
const cancelJob = httpsCallable(functions, "cancelJob");
const checkStripeAccountCapabilities = httpsCallable(functions, "checkStripeAccountCapabilities");

const Chat = ({ applicationId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [parentDetails, setParentDetails] = useState(null);

  useEffect(() => {
    // Fetch parent details
    const fetchParentDetails = async () => {
      try {
        const applicationDoc = await getDoc(doc(db, 'applications', applicationId));

        if (applicationDoc.exists()) {
          const applicationData = applicationDoc.data();
          const parentDoc = await getDoc(doc(db, 'users', applicationData.parentId));
          if (parentDoc.exists()) {
            const parentData = parentDoc.data();
            // Add the uid field from the document ID
            parentData.uid = parentDoc.id;
            setParentDetails(parentData);
          }
        }
      } catch (error) {
        console.error('Error fetching parent details:', error);
      }
    };

    fetchParentDetails();

    const messagesQuery = query(
      collection(db, 'messages'),
      where('applicationId', '==', applicationId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [applicationId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      return;
    }

    if (!parentDetails || !parentDetails.uid) {
      alert('Parent details not loaded yet. Please try again in a moment.');
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You must be logged in to send messages.');
        window.location.href = '/auth';
        return;
      }

      const messageData = {
        applicationId,
        senderId: user.uid,
        recipientId: parentDetails.uid,
        message: newMessage,
        timestamp: new Date(),
      };

      await addDoc(collection(db, 'messages'), messageData);

      setNewMessage(''); // Clear the input after sending
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please check your connection and try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
      <div className="bg-white w-full max-w-md p-4 sm:p-6 rounded-lg shadow-lg relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close chat"
        >
          &times;
        </button>
  
        {/* Parent Info */}
        {parentDetails && (
          <div className="flex items-center mb-6 mt-2">
            <img
              src={parentDetails.profilePictureURL || 'https://via.placeholder.com/50'}
              alt="Parent Profile"
              className="w-12 h-12 rounded-full mr-3 border"
            />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              {parentDetails.name || 'Parent'}
            </h2>
          </div>
        )}
  
        {/* Messages */}
        <div className="chat-container max-h-64 overflow-y-auto mb-4 p-3 pt-6 bg-gray-100 rounded-lg space-y-2 will-change-scroll">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.senderId === auth.currentUser.uid ? 'justify-end' : 'justify-start'
              } group`}
            >
              <div
                className={`relative max-w-[80%] sm:max-w-xs p-2 text-sm rounded-lg ${
                  msg.senderId === auth.currentUser.uid
                    ? 'bg-blue-500 text-white rounded-br-none'
                    : 'bg-gray-200 text-gray-800 rounded-bl-none'
                }`}
              >
                {msg.message}
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 text-xs bg-gray-800 text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 shadow-lg border border-gray-600 pointer-events-none">
                  {new Date(msg.timestamp?.toDate()).toLocaleString()}
                  {/* Arrow pointing down */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
  
        {/* Message Input */}
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-grow p-3 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button
            onClick={sendMessage}
            className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-150"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
  
};


const SitterJobs = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [filter, setFilter] = useState('Pending Applications'); // Default filter
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null); // "chat" or "workLog"
  const [selectedJob, setSelectedJob] = useState(null);
  const [workLogs, setWorkLogs] = useState([]);
  const [activeApplicationId, setActiveApplicationId] = useState(null);
  const [review, setReview] = useState({rating: 0, comment: ''});
  const [currentUser, setCurrentUser] = useState(null);
  const [isTimesModalOpen, setIsTimesModalOpen] = useState(false);
  const [selectedJobForTimes, setSelectedJobForTimes] = useState(null);

  const [jobOffersCount, setJobOffersCount] = useState(0);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [isCheckingStripeStatus, setIsCheckingStripeStatus] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingLink, setOnboardingLink] = useState(null);

  // Navigation uses window.location.href for reliability

  function formatLocalDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00'); // Prevent timezone offset
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });
  }



  const fetchApplications = async (filter = 'All') => {
    try {

      console.log('Fetching applications...' + filter);
      const user = auth.currentUser;

      if (!user) {
        alert('User not authenticated');
        window.location.href = '/auth';
        return;
      }

      // Fetch user role
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        alert('User data not found.');
        return;
      }
      const userData = userDoc.data();
      const userType = userData.userType;
      if (userType !== 'sitter') {
        alert('Only sitters can view this page.');
        window.location.href = '/auth'; // Redirect to auth for unauthorized access
        return;
      }
      setCurrentUser(userData);

      // Fetch applications submitted by the sitter
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('sitterId', '==', user.uid),
        orderBy('appliedAt', 'desc')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);

      let offerCount = 0;

      // Batch fetch all parent details
      const parentIds = [...new Set(applicationsSnapshot.docs.map(doc => doc.data().parentId))];
      const parentPromises = parentIds.map(async (parentId) => {
        const parentDoc = await getDoc(doc(db, 'users', parentId));
        if (parentDoc.exists()) {
          const parentData = parentDoc.data();
          return {
            parentId,
            name: parentData.name || 'Unknown',
            profilePictureURL: parentData.profilePictureURL || null,
          };
        }
        return { parentId, name: 'Unknown', profilePictureURL: null };
      });
      const parentResults = await Promise.all(parentPromises);
      const parentMap = parentResults.reduce((acc, parent) => {
        acc[parent.parentId] = parent;
        return acc;
      }, {});

      const applicationsList = applicationsSnapshot.docs.map((document) => {
        const application = { id: document.id, ...document.data() };

        offerCount += application.status === 'Awaiting Sitter Confirmation' ? 1 : 0;

        // Use cached parent data
        const parentData = parentMap[application.parentId];
        if (parentData) {
          application.parentProfilePictureURL = parentData.profilePictureURL;
          application.parentName = parentData.name;
        }

        // Set default city/state (can be fetched later if needed)
        application.jobCity ??= null;
        application.jobState ??= null;

        return application;
      });

      setJobOffersCount(offerCount);

      setApplications(applicationsList);
      
      // handleFilterChange(filter);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStripeAccountStatus = async (openOnboardingIfNeeded = false) => {
    if (!currentUser?.stripeAccountId) return;
    
    setIsCheckingStripeStatus(true);
    try {
      const response = await checkStripeAccountCapabilities({
        stripeAccountId: currentUser.stripeAccountId,
        siteUrl: window.location.origin
      });
      
      if (response.data.success) {
        const { hasTransfers, hasCardPayments, isFullyOnboarded, onboardingLink } = response.data;
        const needsAttention = !hasTransfers || !hasCardPayments || !isFullyOnboarded;
        
        setStripeAccountStatus({
          hasTransfers,
          hasCardPayments,
          isFullyOnboarded,
          needsAttention
        });

        // If onboarding is needed and user requested it, open the link
        if (openOnboardingIfNeeded && needsAttention && onboardingLink) {
          window.open(onboardingLink, "_blank");
        }
      }
    } catch (error) {
      console.error("Error checking Stripe status:", error);
    } finally {
      setIsCheckingStripeStatus(false);
    }
  };

  const refreshStripeStatus = () => {
    checkStripeAccountStatus();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchApplications("All");
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentUser?.stripeAccountId) {
      checkStripeAccountStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    handleFilterChange(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, filter]);

  const rescindApplication = async (applicationId) => {
    if (window.confirm('Are you sure you want to rescind this application?')) {
      try {
        await deleteDoc(doc(db, 'applications', applicationId));
        alert('Application rescinded successfully.');
        // Refresh the application list
        await fetchApplications(filter);
        handleFilterChange(filter);
        
      } catch (error) {
        console.error('Error rescinding application:', error);
        alert('Failed to rescind the application. Please try again.');
      }
    }
  };

  const handleCancelJob = async (application) => {
    // Check if cancellation is within 24 hours of job start
    let feeMessage = "";
    if (application.jobStartDate && application.jobStartTime) {
      const jobStartDate = new Date(`${application.jobStartDate} ${application.jobStartTime}`);
      const now = new Date();
      const hoursUntilStart = (jobStartDate - now) / (1000 * 60 * 60);
      
      if (hoursUntilStart <= 24 && hoursUntilStart >= 0) {
        const cancellationFee = application.cancellationFee || 25;
        feeMessage = ` A $${cancellationFee} cancellation fee will be charged.`;
      } else {
        feeMessage = " No fee will be charged as cancellation is more than 24 hours before job start.";
      }
    } else {
      // Default to showing fee if we can't determine job start time
      const cancellationFee = application.cancellationFee || 25;
      feeMessage = ` A $${cancellationFee} cancellation fee will be charged.`;
    }
    
    const confirmCancel = window.confirm(`Are you sure you want to cancel this job?${feeMessage}`);
  
    if (!confirmCancel) return;
  
    try {
      const response = await cancelJob({
        userId: auth.currentUser.uid,
        applicationId: application.id,
      });

      console.log("Cancel job response:", response.data);

      if (!response.data) {
        throw new Error("No response data received");
      }

      if (response.data.success) {
        const feeCharged = response.data.cancellationFee || 0;
        if (feeCharged > 0) {
          alert(`Job cancelled successfully. A $${feeCharged} cancellation fee has been charged.`);
        } else {
          alert(`Job cancelled successfully. No fee was charged as cancellation was more than 24 hours before job start.`);
        }
        await fetchApplications(filter);
        handleFilterChange(filter);
      } else {
        let errorMessage = response.data.message || "Cancellation failed. Please try again.";
        
        // Provide more helpful error message for customer-related errors
        if (errorMessage.includes("No such customer") || errorMessage.includes("customer")) {
          errorMessage = "There was an issue with your payment account. Please contact support or try again in a few minutes. The system is updating your payment information.";
        }
        
        alert(errorMessage);
        console.error('Cancellation failed:', response.data);
      }
    } catch (error) {
      console.error("Cancellation error:", error);
      const errorMessage = error.message || "An error occurred while cancelling the job. Please try again.";
      alert(errorMessage);
    }
  };
  

  const handleModalOpen = (type, applicationId) => {
    setModalType(type)
    setActiveApplicationId(applicationId);
  };

  const handleModalClose = () => {
    setModalType(null);
    setActiveApplicationId(null);
  };

  const validateWorkLogs = () => {
    for (const log of workLogs) {
      if (!log.date || !log.clockIn || !log.clockOut) {
        alert('Please fill in all fields for each work log.');
        return false;
      }
    }
    return true;
  };

  const submitWorkLogs = async () => {
    if (!validateWorkLogs()) return;
  
    if (workLogs.length === 0) {
      alert('Please add at least one work log entry.');
      return;
    }
  
    try {
      // ✅ Group logs by date and aggregate time ranges
      const groupedLogs = {};
  
      workLogs.forEach((log) => {
        const { date, clockIn, clockOut } = log;
        if (!date || !clockIn || !clockOut) return;
  
        // Handle overnight clock-out
        const start = new Date(`1970-01-01T${clockIn}`);
        let end = new Date(`1970-01-01T${clockOut}`);
        if (end <= start) end.setDate(end.getDate() + 1);
  
        const hoursWorked = parseFloat(((end - start) / (1000 * 60 * 60)).toFixed(2));
  
        if (!groupedLogs[date]) {
          groupedLogs[date] = {
            date,
            timeRanges: [],
          };
        }
  
        groupedLogs[date].timeRanges.push({
          clockIn,
          clockOut,
          hoursWorked,
        });
      });
  
      // Flatten into array format
      const formattedWorkLogs = Object.values(groupedLogs);

      // Enforce the 3-hour minimum. Families book a 3-hour minimum, so the
      // sitter is guaranteed at least 3 hours of pay per day even if she was
      // relieved of duties early. Overnight care split at midnight (day one
      // ends 11:59 PM, next day starts 12:00 AM) counts as ONE session — the
      // combined hours must reach 3, not each calendar day separately.
      {
        const sortedDates = Object.keys(groupedLogs).sort();
        const isNextDay = (a, b) => {
          const d = new Date(a + 'T00:00:00');
          d.setDate(d.getDate() + 1);
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` === b;
        };
        const sessions = [];
        let current = null;
        for (const date of sortedDates) {
          const ranges = groupedLogs[date].timeRanges;
          const minutes = ranges.reduce((s, r) => s + (r.hoursWorked || 0) * 60, 0);
          const startsAtMidnight = ranges.some((r) => r.clockIn === '00:00');
          const endsAtMidnight = ranges.some((r) => r.clockOut === '23:59');
          if (current && current.endsAtMidnight && startsAtMidnight && isNextDay(current.lastDate, date)) {
            // Overnight continuation — merge into the previous session.
            current.minutes += minutes;
            current.lastDate = date;
            current.endsAtMidnight = endsAtMidnight;
            current.label += ` + ${date}`;
          } else {
            if (current) sessions.push(current);
            current = { label: date, lastDate: date, minutes, endsAtMidnight };
          }
        }
        if (current) sessions.push(current);

        for (const s of sessions) {
          if (s.minutes < 180) {
            alert(`You're guaranteed a minimum of 3 hours of pay — the family booked a 3-hour minimum, even if you were relieved of duties early.\n\n${s.label} totals ${(s.minutes / 60).toFixed(2)} hours. Please adjust your times to reflect at least 3 hours.`);
            return;
          }
        }
      }

      // Calculate total hours
      const totalHours = formattedWorkLogs.reduce((total, log) => {
        const daily = log.timeRanges.reduce((sum, r) => sum + (r.hoursWorked || 0), 0);
        return total + daily;
      }, 0).toFixed(2);

      const jobRef = doc(db, 'applications', selectedJob.id);

      // Re-read the application on the server before writing — the local
      // selectedJob may be stale (parent paid or cancelled in another tab).
      // If it's already Paid/Refunded, abort so we don't overwrite money fields.
      const freshSnap = await getDoc(jobRef);
      if (!freshSnap.exists()) {
        alert('This application no longer exists.');
        return;
      }
      const freshApp = freshSnap.data();
      if (freshApp.status === 'Paid' || freshApp.status === 'Refunded' || freshApp.status === 'Parent Cancelled' || freshApp.status === 'Sitter Cancelled') {
        alert(`Cannot edit — the job has already been ${freshApp.status.toLowerCase()}.`);
        handleModalClose();
        await fetchApplications(filter);
        return;
      }
      const isEdit = freshApp.status === 'Completed';

      // Preserve any tip already recorded on the application. Overwriting
      // sitterPayout without it would silently erase the tip.
      const existingTip = Number(freshApp.tipAmount) || 0;
      const basePay = Number(totalHours) * Number(selectedJob.jobPayRange);
      const sitterPayout = (basePay + existingTip).toFixed(2);

      const updateFields = {
        workLogs: formattedWorkLogs,
        totalHoursWorked: totalHours,
        sitterPayout: sitterPayout,
      };
      if (!isEdit) {
        updateFields.status = 'Completed';
      } else {
        updateFields.workLogUpdatedAt = new Date();
      }
      await updateDoc(jobRef, updateFields);

      // Only ask for a review on the initial submit — don't spam reviews on edits.
      if (!isEdit && (review.rating > 0 || review.comment.trim() !== '')) {
        await addDoc(collection(db, 'reviews'), {
          subjectId: selectedJob.parentId,
          reviewerId: auth.currentUser?.uid,
          reviewerProfilePicture: selectedJob.sitterProfilePictureURL,
          reviewerName: selectedJob.sitterName,
          rating: review.rating,
          comment: review.comment,
          createdAt: new Date(),
        });
      }

      alert(isEdit ? 'Work log updated!' : 'Work logs submitted successfully!');
      handleModalClose();
      setFilter('Pending Hours');
      await fetchApplications('Pending Hours');
      handleFilterChange('Pending Hours');
    } catch (error) {
      console.error('Error submitting work logs:', error);
      alert('Failed to submit work logs. Please try again.');
    }
  };
  

  const handleFilterChange = (status) => {
    setFilter(status); // Update the button's visual state

    // Membership is decided ENTIRELY by the shared lifecycle module
    // (belongsInSitterTab) so a status can never belong to zero tabs and
    // silently vanish — the invariant is covered by applicationLifecycle.test.js.
    let filtered = applications.filter((application) => belongsInSitterTab(application, status));

    // Per-tab sort order.
    switch (status) {
      case 'Pending Applications':
        filtered.sort((a, b) => (b.appliedAt?.seconds || 0) - (a.appliedAt?.seconds || 0));
        break;
      case 'Job Offers':
      case 'Active Jobs':
        filtered.sort((a, b) => new Date(a.jobStartDate + 'T00:00:00') - new Date(b.jobStartDate + 'T00:00:00'));
        break;
      case 'Pending Hours':
      case 'Job History':
        filtered.sort((a, b) => new Date(b.jobEndDate + 'T00:00:00') - new Date(a.jobEndDate + 'T00:00:00'));
        break;
      case 'Closed':
        filtered.sort((a, b) => {
          const at = a.closedAt?.seconds || a.appliedAt?.seconds || 0;
          const bt = b.closedAt?.seconds || b.appliedAt?.seconds || 0;
          return bt - at;
        });
        break;
      default:
        filtered = applications.slice().sort((a, b) => new Date(a.jobStartDate + 'T00:00:00') - new Date(b.jobStartDate + 'T00:00:00'));
    }

    setFilteredApplications(filtered);
  };

  const acceptJobOffer = async (applicationData) => {
    try {
      console.log('Starting acceptJobOffer for application:', applicationData.id);
      
      // CRITICAL: Check current application status before allowing acceptance
      const applicationRef = doc(db, 'applications', applicationData.id);
      const applicationSnapshot = await getDoc(applicationRef);
      
      if (!applicationSnapshot.exists()) {
        alert('This job application no longer exists.');
        await fetchApplications();
        return;
      }
      
      const currentApplication = applicationSnapshot.data();
      
      // Prevent accepting if already denied, expired, or not in the correct status
      if (currentApplication.status === 'Sitter Denied' || currentApplication.status === 'Family Denied') {
        alert('This job offer has expired or been denied. Please refresh the page to see the latest status.');
        await fetchApplications();
        return;
      }
      
      if (currentApplication.status !== 'Awaiting Sitter Confirmation') {
        alert(`This job offer is no longer available. Current status: ${currentApplication.status}`);
        await fetchApplications();
        return;
      }
      
      console.log('Current user Stripe account ID:', currentUser?.stripeAccountId);
      console.log('Current user payment method ID:', currentUser?.paymentMethodId);
      
      // Check if user has a valid payment method (card) on file
      if (!currentUser || !currentUser.paymentMethodId || currentUser.paymentMethodId.trim() === '') {
        alert("You must add a payment method (card) on your profile before accepting a job. This keeps you eligible for jobs and covers any potential cancellation fees.");
        window.location.href = `/profile/${auth.currentUser.uid}`;
        return;
      }
      
      // First check if the sitter has a Stripe account
      if (!currentUser.stripeAccountId) {
        alert("You must set up your Stripe account for payouts before accepting a job.");
        window.location.href = `/profile/${auth.currentUser.uid}`;
        return;
      }

      console.log('Checking Stripe account capabilities...');
      // Check Stripe account capabilities
      const capabilitiesResponse = await checkStripeAccountCapabilities({
        stripeAccountId: currentUser.stripeAccountId,
        siteUrl: window.location.origin
      });

      console.log('Capabilities response:', capabilitiesResponse);

      if (!capabilitiesResponse.data.success) {
        console.error('Capabilities check failed:', capabilitiesResponse);
        alert("Error checking Stripe account status. Please try again.");
        return;
      }

      const { hasTransfers, hasCardPayments, isFullyOnboarded, onboardingLink } = capabilitiesResponse.data;
      console.log('Capabilities:', { hasTransfers, hasCardPayments, isFullyOnboarded });

      // Check if the account has the required capabilities
      if (!hasTransfers || !hasCardPayments || !isFullyOnboarded) {
        const missingCapabilities = [];
        if (!hasTransfers) missingCapabilities.push("transfers");
        if (!hasCardPayments) missingCapabilities.push("card payments");
        if (!isFullyOnboarded) missingCapabilities.push("full onboarding");

        console.log('Missing capabilities:', missingCapabilities);
        const message = `Your Stripe account is not fully set up. Missing: ${missingCapabilities.join(", ")}.\n\nYou need to complete your Stripe onboarding to accept jobs.`;
        
        if (onboardingLink) {
          // Show custom confirmation modal
          setOnboardingLink(onboardingLink);
          setShowOnboardingModal(true);
        } else {
          alert(message + "\n\nPlease visit https://dashboard.stripe.com/onboarding to complete your setup.");
        }
        return;
      }

      console.log('All checks passed, atomically claiming the job...');
      // Use a transaction so two sitters can't both accept the same job:
      // re-read the job inside the txn and abort if it's no longer Open.
      const jobRef = doc(db, 'jobs', applicationData.jobId);
      try {
        await runTransaction(db, async (txn) => {
          const jobSnap = await txn.get(jobRef);
          if (!jobSnap.exists()) {
            throw new Error('Job no longer exists.');
          }
          const jobData = jobSnap.data();
          if (jobData.status !== 'Open') {
            throw new Error('This job has already been filled by another sitter.');
          }
          txn.update(applicationRef, { status: 'Active', sitterStripeAccountId: currentUser.stripeAccountId });
          txn.update(jobRef, { status: 'Filled' });
        });
      } catch (txnErr) {
        console.error('Job accept transaction aborted:', txnErr.message);
        alert(txnErr.message || 'Could not accept the job — please refresh and try again.');
        return;
      }
      
      console.log('Job accepted successfully!');
      alert('You have accepted the job offer!');
      setFilter('Active Jobs');
      await fetchApplications('Active Jobs');
      handleFilterChange('Active Jobs');
    } catch (error) {
      console.error('Error accepting job offer:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      if (error.message?.includes('Stripe')) {
        alert('There was an issue with your Stripe account. Please contact support or try setting up your account again.');
      } else {
        alert(`Failed to accept the job offer. Error: ${error.message || 'Unknown error'}. Please try again.`);
      }
    }
  };

  const declineJobOffer = async (applicationId) => {
    try {
      const applicationRef = doc(db, 'applications', applicationId);
      await updateDoc(applicationRef, { status: 'Sitter Denied' });


      
      alert('You have declined the job offer.');
      await fetchApplications(filter);
      handleFilterChange(filter);
    } catch (error) {
      console.error('Error declining job offer:', error);
      alert('Failed to decline the job offer. Please try again.');
    }
  };
  
  const openWorkLogModal = (job) => {
    setSelectedJob(job);

    // If the sitter is editing an already-submitted work log, prepopulate
    // with what she previously submitted.
    if (job.status === 'Completed' && Array.isArray(job.workLogs) && job.workLogs.length > 0) {
      const existingLogs = [];
      job.workLogs.forEach((entry) => {
        const ranges = Array.isArray(entry.timeRanges) ? entry.timeRanges : [{
          clockIn: entry.clockIn,
          clockOut: entry.clockOut,
          hoursWorked: entry.hoursWorked,
        }];
        ranges.forEach((r) => {
          if (r.clockIn && r.clockOut) {
            existingLogs.push({
              date: entry.date,
              clockIn: r.clockIn,
              clockOut: r.clockOut,
              hoursWorked: r.hoursWorked || 0,
            });
          }
        });
      });
      setWorkLogs(existingLogs);
    } else if (job.jobSchedule && Object.keys(job.jobSchedule).length > 0) {
      // First-time submission: prepopulate from the job schedule.
      const prepopulatedLogs = [];
      const sortedDates = Object.keys(job.jobSchedule).sort((a, b) => new Date(a) - new Date(b));
      sortedDates.forEach(date => {
        const timeRanges = Array.isArray(job.jobSchedule[date])
          ? job.jobSchedule[date]
          : [job.jobSchedule[date]];
        timeRanges.forEach((range) => {
          if (range.start && range.end) {
            prepopulatedLogs.push({
              date: date,
              clockIn: range.start,
              clockOut: range.end,
              hoursWorked: 0
            });
          }
        });
      });
      setWorkLogs(prepopulatedLogs);
    } else {
      setWorkLogs([]);
    }

    setReview({rating: 0, comment: ''});
    handleModalOpen('workLog', job.id);
  };

  // const renderStars = (rating, onClick) => {
  //   return (
  //     <div className="flex">
  //       {[...Array(5)].map((_, i) => (
  //         <FaStar
  //           key={i}
  //           className={`cursor-pointer text-xl ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
  //           onClick={() => onClick && onClick(i + 1)}
  //         />
  //       ))}
  //     </div>
  //   );
  // };

  const formatTimeToAMPM = (time24) => {
    if (!time24) return '';
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const JobTimesModal = ({ isOpen, onClose, application }) => {
    // Handle escape key - must be called before early return
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape' && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isOpen, onClose]);

    if (!isOpen || !application?.jobSchedule) return null;

    const handleOverlayClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const handleModalClick = (e) => {
      e.stopPropagation();
    };
    
    return (
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50"
        onClick={handleOverlayClick}
      >
        <div 
          className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all duration-300 scale-100"
          onClick={handleModalClick}
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Job Schedule</h2>
                  <p className="text-sm text-slate-600">{application.jobTitle}</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {application.jobSchedule && Object.keys(application.jobSchedule).length > 0 ? (
              <div className="space-y-4">
                {Object.keys(application.jobSchedule)
                  .sort((a, b) => new Date(a) - new Date(b))
                  .map((date) => {
                    const ranges = Array.isArray(application.jobSchedule[date])
                      ? application.jobSchedule[date]
                      : [application.jobSchedule[date]];

                    return (
                      <div key={date} className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <h3 className="font-semibold text-slate-800">{formatLocalDate(date)}</h3>
                        </div>

                        <div className="space-y-2">
                          {ranges.map((range, index) => (
                            <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                              <div className="flex items-center space-x-3">
                                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <span className="text-slate-700 font-medium">
                                  {formatTimeToAMPM(range.start)} - {formatTimeToAMPM(range.end)}
                                </span>
                              </div>
                              <div className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                Range {index + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-500 font-medium">No days scheduled</p>
                <p className="text-sm text-slate-400">This job doesn't have a schedule set yet.</p>
              </div>
            )}
          </div>

          {/* Sticky Footer */}
          <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-t border-slate-200/50 rounded-b-2xl flex-shrink-0">
            <div className="flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-slate-500/25"
              >
                <span className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Close</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const addWorkLog = () => {
    setWorkLogs([
      ...workLogs,
      { date: '', clockIn: '', clockOut: '', hoursWorked: 0},
    ]);
  };

  const addCustomWorkLog = () => {
    // Add a work log with today's date for additional hours
    const today = new Date().toISOString().split('T')[0];
    setWorkLogs([
      ...workLogs,
      { date: today, clockIn: '', clockOut: '', hoursWorked: 0},
    ]);
  };

  const duplicateWorkLog = (index) => {
    const logToDuplicate = workLogs[index];
    if (logToDuplicate.date && logToDuplicate.clockIn && logToDuplicate.clockOut) {
      // Create a new entry with the same times but next day
      const currentDate = new Date(logToDuplicate.date);
      const nextDate = new Date(currentDate);
      nextDate.setDate(currentDate.getDate() + 1);
      
      const newLog = {
        date: nextDate.toISOString().split('T')[0],
        clockIn: logToDuplicate.clockIn,
        clockOut: logToDuplicate.clockOut,
        hoursWorked: 0
      };
      
      setWorkLogs([...workLogs, newLog]);
    }
  };

  const removeWorkLog = (index) => {
    setWorkLogs((prevLogs) => prevLogs.filter((_, i) => i !== index));
  };

  const updateWorkLog = (index, field, value) => {
    const updatedLogs = [...workLogs];
    updatedLogs[index][field] = value;
    setWorkLogs(updatedLogs);
  };

  const calculateTotalHours = (workLogs) => {
    const grouped = {};
  
    workLogs.forEach((log) => {
      const { date, clockIn, clockOut } = log;
      if (!date || !clockIn || !clockOut) return;
  
      const start = new Date(`1970-01-01T${clockIn}`);
      let end = new Date(`1970-01-01T${clockOut}`);
      if (end <= start) end.setDate(end.getDate() + 1);
  
      const hours = parseFloat(((end - start) / (1000 * 60 * 60)).toFixed(2));
  
      if (!grouped[date]) grouped[date] = 0;
      grouped[date] += hours;
    });
  
    return Object.values(grouped).reduce((sum, val) => sum + val, 0).toFixed(2);
  };
  
  


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center sm:text-left mb-8">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-2">
            Your Jobs
          </h2>
          <p className="text-slate-600 text-lg">
            Manage your applications and track your work
          </p>
        </div>
  
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-3 justify-center sm:justify-start mb-8">
          {['Pending Applications', 'Job Offers', 'Active Jobs', 'Pending Hours', 'Job History', 'Closed'].map((status) => (
            <button
              key={status}
              onClick={() => handleFilterChange(status)}
              className={`relative group px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                filter === status 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'bg-white/80 backdrop-blur-sm border border-white/40 text-slate-700 hover:bg-white hover:shadow-lg'
              }`}
            >
              {status}

              {status === 'Job Offers' && jobOffersCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg animate-pulse">
                  {jobOffersCount}
                </span>
              )}
            </button>
          ))}
        </div>
  
      {/* View Payouts button for Job History tab */}
      {filter === 'Job History' && (
        <div className="flex justify-end mb-4">
          <a
            href="https://connect.stripe.com/express_login"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            View Payouts
          </a>
        </div>
      )}

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No applications found</h3>
          <p className="text-slate-500">
            {filter !== 'All' ? `No applications with status "${filter}"` : 'You haven\'t applied to any jobs yet.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-6">
          {filteredApplications.map((application) => (
            <li
              key={application.id}
              className="group bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
            >
              {/* Status Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50">
                <div className="flex items-center justify-between flex-wrap gap-y-1">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      application.status === 'Active' ? 'bg-green-500 animate-pulse' :
                      application.status === 'Completed' ? 'bg-blue-500' :
                      application.status === 'Paid' ? 'bg-emerald-500' :
                      application.status === 'Awaiting Sitter Confirmation' ? 'bg-yellow-500 animate-pulse' :
                      'bg-slate-400'
                    }`}></div>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                      application.status === 'Active' ? 'bg-green-100 text-green-800' :
                      application.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                      application.status === 'Paid' ? 'bg-emerald-100 text-emerald-800' :
                      application.status === 'Awaiting Sitter Confirmation' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {application.status}
                    </span>
                    {STATUS_TIPS_SITTER[application.status] && (
                      <InfoTip align="left" text={STATUS_TIPS_SITTER[application.status]} />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    Applied {new Date(application.appliedAt.seconds * 1000).toLocaleDateString()}
                  </div>
                </div>
                {/* Reason line for closed/declined applications so they never
                    just disappear without explanation */}
                {CLOSED_REASON_SITTER[application.status] && (
                  <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    {CLOSED_REASON_SITTER[application.status]}
                  </div>
                )}
                {/* 24-hour expiration warning for pending job offers */}
                {application.status === 'Awaiting Sitter Confirmation' && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-orange-800 font-medium">
                        ⏰ This offer expires in 24 hours! Please accept or decline soon.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Profile Picture */}
                  {application.parentProfilePictureURL && (
                    <a href={`/profile/${application.parentId}`} className="self-center sm:self-start">
                      <div className="relative group">
                        <img
                          src={application.parentProfilePictureURL}
                          alt="Parent Profile"
                          className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover shadow-lg border-4 border-white group-hover:border-blue-200 transition-all duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    </a>
                  )}
                  <div className="flex-1 text-sm sm:text-base">
                    {/* Parent Info */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">
                          <span className="font-medium">Parent:</span> {application.parentName}
                        </p>
                        <h3 className="text-2xl font-bold text-slate-800 mb-2">
                          {application.jobTitle}
                        </h3>
                      </div>
                      <div className="mt-3 sm:mt-0">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold text-lg">
                          ${application.jobPayRange}/hr
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 mt-1">{application.jobDescription}</p>
                    <div className="mt-3 space-y-1 text-gray-700">
                      <p><strong>Start Date:</strong> {formatLocalDate(application.jobStartDate)}</p>
                      <p><strong>Start Time:</strong> {formatTimeToAMPM(application.jobStartTime)}</p>
                      <p><strong>End Date:</strong> {formatLocalDate(application.jobEndDate)}</p>
                      <p>
                        <strong>Location:</strong>{' '}
                        {application.status === 'Pending' || application.status === 'Awaiting Sitter Confirmation'
                          ? [application.jobCity, application.jobState, application.jobZipCode]
                              .filter(Boolean)
                              .join(', ')
                          : application.jobAddress || [application.jobCity, application.jobState]
                              .filter(Boolean)
                              .join(', ')
                        }
                      </p>
                      {((application.jobChildren && application.jobChildren.length > 0) || (application.jobChildrenAges && application.jobChildrenAges.length > 0)) && (
                        <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Children's Ages</h4>
                          <div className="flex flex-wrap gap-2">
                            {application.jobChildren ? (
                              application.jobChildren.map((child, index) => (
                                <span key={child.id || index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {child.name && `${child.name}: `}{formatAge(child.age)} old
                                </span>
                              ))
                            ) : (
                              application.jobChildrenAges.map((age, index) => (
                                <span key={index} className="bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 shadow-sm">
                                  {age.value} {age.unit} old
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                      {/* Message */}
                      {application.message && (
                        <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-xl">
                          <p className="text-sm text-blue-800">
                            <span className="font-semibold">Message:</span> {application.message}
                          </p>
                        </div>
                      )}
                    </div>

                    {application.status === 'Paid' && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm text-emerald-700 font-semibold">Payment Completed</p>
                              <p className="text-lg font-bold text-emerald-800">${application.sitterPayout}</p>
                            </div>
                          </div>
                          {application.stripeReceiptUrl && (
                            <a
                              href={application.stripeReceiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-white border border-emerald-300 rounded-lg text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Receipt
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {(application.status === 'Completed' || application.status === "Paid") && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Work Summary</h4>
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            {application.totalHoursWorked || 0} hours
                          </span>
                        </div>
                        
                        {application.workLogs && application.workLogs.length > 0 ? (
                          <div className="space-y-2">
                            {application.workLogs.map((log, index) => (
                              <div key={index} className="bg-white p-3 rounded-lg border border-slate-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-slate-800">{log.date}</span>
                                </div>
                                <div className="space-y-1">
                                  {(Array.isArray(log.timeRanges) ? log.timeRanges : [{
                                    clockIn: log.clockIn,
                                    clockOut: log.clockOut,
                                    hoursWorked: log.hoursWorked,
                                  }]).map((range, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                      <span className="text-slate-600">
                                        {formatTimeToAMPM(range.clockIn)} - {formatTimeToAMPM(range.clockOut)}
                                      </span>
                                      <span className="font-medium text-slate-800">
                                        {range.hoursWorked || 0} hours
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm italic">No work log available.</p>
                        )}
                      </div>
                    )}

                    {application.status === 'Completed' && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm text-blue-700 font-semibold">Pending Payout</p>
                            <p className="text-lg font-bold text-blue-800">${application.sitterPayout}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="mt-6">
                      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                      {application.status === 'Awaiting Sitter Confirmation' && (
                        <>
                          {/* Stripe Account Status */}
                          {stripeAccountStatus && (
                            <div className={`w-full mb-4 p-4 rounded-xl border ${
                              stripeAccountStatus.needsAttention 
                                ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' 
                                : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                            }`}>
                              <div className="flex items-center space-x-3">
                                {stripeAccountStatus.needsAttention ? (
                                  <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className={`text-sm font-semibold ${
                                    stripeAccountStatus.needsAttention ? 'text-yellow-800' : 'text-green-800'
                                  }`}>
                                    Stripe Account Status
                                  </p>
                                  <p className={`text-sm ${
                                    stripeAccountStatus.needsAttention ? 'text-yellow-700' : 'text-green-700'
                                  }`}>
                                    {stripeAccountStatus.needsAttention 
                                      ? 'Your account needs verification to accept jobs.' 
                                      : 'Your account is fully verified and ready to accept jobs!'
                                    }
                                  </p>
                                </div>
                                <button 
                                  onClick={refreshStripeStatus}
                                  disabled={isCheckingStripeStatus}
                                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                    stripeAccountStatus.needsAttention 
                                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                                      : 'bg-green-500 hover:bg-green-600 text-white'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {isCheckingStripeStatus ? (
                                    <span className="flex items-center space-x-2">
                                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      <span>Checking...</span>
                                    </span>
                                  ) : (
                                    stripeAccountStatus.needsAttention ? 'Check Status' : 'Refresh Status'
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => acceptJobOffer(application)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Accept Job</span>
                            </span>
                          </button>
                          <button
                            onClick={() => declineJobOffer(application.id)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span>Decline</span>
                            </span>
                          </button>
                        </>
                      )}

                      {filter === 'Active Jobs' && (
                        <>
                          <button
                            onClick={() => openWorkLogModal(application)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              <span>Submit Work Log</span>
                            </span>
                          </button>
                          <button
                            onClick={() => handleCancelJob(application)}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25"
                          >
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span>Cancel Job</span>
                            </span>
                          </button>
                        </>
                      )}

                      {filter === 'Pending Hours' && application.status === 'Completed' && (
                        <button
                          onClick={() => openWorkLogModal(application)}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-amber-500/25"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <span className="flex items-center justify-center space-x-2 pointer-events-none">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit Work Log</span>
                          </span>
                        </button>
                      )}

                      {filter === 'Pending Applications' && application.status === 'Pending' && (
                        <button
                          onClick={() => rescindApplication(application.id)}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Rescind Application</span>
                          </span>
                        </button>
                      )}

                      {/* View Schedule Button */}
                      {(application.status === 'Pending' || application.status === 'Awaiting Sitter Confirmation' || application.status === 'Active') && (
                        <button
                          onClick={() => {
                            setSelectedJobForTimes(application);
                            setIsTimesModalOpen(true);
                          }}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-violet-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-purple-500/25"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>View Schedule</span>
                          </span>
                        </button>
                      )}

                      {/* Message Button - Show for Active jobs that haven't been paid */}
                      {application.status === 'Active' && (
                        <button
                          onClick={() => {
                            setActiveApplicationId(application.id);
                            setModalType('chat');
                          }}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/25"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9 9 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span>Message Parent</span>
                          </span>
                        </button>
                      )}

                      {/* Message Button - Show for Completed jobs that haven't been paid */}
                      {application.status === 'Completed' && (
                        <button
                          onClick={() => {
                            setActiveApplicationId(application.id);
                            setModalType('chat');
                          }}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-amber-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-orange-500/25"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9 9 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span>Message Parent</span>
                          </span>
                        </button>
                      )}

                      {/* View Profile Button */}
                      {application.parentId && (
                        <a
                          href={`/profile/${application.parentId}`}
                          className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl hover:from-slate-600 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-slate-500/25 text-center"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>View Parent Profile</span>
                          </span>
                        </a>
                      )}
                      </div>
                      </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
  
      {modalType === 'workLog' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-sm border border-white/40 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col transform transition-all duration-300 scale-100">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedJob?.status === 'Completed' ? 'Edit Work Log' : 'Submit Work Logs'}</h3>
                    <p className="text-sm text-slate-600">{selectedJob?.status === 'Completed' ? 'Adjust your hours before payment' : 'Track your work hours and leave a review'}</p>
                  </div>
                </div>
                <button
                  onClick={handleModalClose}
                  className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors duration-200"
                >
                  <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Work Logs Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-slate-800">Work Hours</h4>
                  <div className="flex space-x-3">
                    <button
                      onClick={addWorkLog}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Add Hours</span>
                      </span>
                    </button>
                    
                    <button
                      onClick={addCustomWorkLog}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-xl hover:from-green-600 hover:to-emerald-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-green-500/25"
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Add Custom Hours</span>
                      </span>
                    </button>
                  </div>
                </div>

                {/* Information about prepopulated times */}
                {workLogs.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium">Schedule Prepopulated from Job Posting</p>
                        <p className="text-blue-700">Dates, clock-in, and clock-out times have been automatically filled based on the original job schedule. You can edit <strong>any field</strong> if your actual work differed from the scheduled times.</p>
                        <div className="mt-2 text-xs text-blue-700 space-y-1">
                          <p>• <strong>Dates:</strong> Change if you worked on different days than scheduled</p>
                          <p>• <strong>Times:</strong> Modify if your actual start/end times differed</p>
                          <p>• <strong>Add/Remove:</strong> Use buttons above to add extra work days or remove unused entries</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {workLogs.map((log, index) => (
                    <div key={index} className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Date Input */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            <span className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>Date</span>
                            </span>
                          </label>
                          <input
                            type="date"
                            value={log.date}
                            onChange={(e) => updateWorkLog(index, 'date', e.target.value)}
                            className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200"
                          />
                          

                        </div>

                        {/* Clock In Input */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            <span className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Clock In</span>
                            </span>
                          </label>
                          <input
                            type="time"
                            value={log.clockIn}
                            onChange={(e) => {
                              const newTime = e.target.value;
                              const clockOut = log.clockOut;

                              if (clockOut && newTime >= clockOut) {
                                alert('Clock-in must be before clock-out and within the same day.');
                                return;
                              }

                              updateWorkLog(index, 'clockIn', newTime);
                            }}
                            className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200"
                          />
                        </div>

                        {/* Clock Out Input */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            <span className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Clock Out</span>
                            </span>
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="time"
                              value={log.clockOut}
                              onChange={(e) => {
                                const newTime = e.target.value;
                                const clockIn = log.clockIn;

                                if (clockIn && newTime <= clockIn) {
                                  alert('Clock-out must be after clock-in and within the same day.');
                                  return;
                                }

                                updateWorkLog(index, 'clockOut', newTime);
                              }}
                              className="flex-1 p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 transition-all duration-200"
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => duplicateWorkLog(index)}
                                className="px-3 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white font-medium rounded-lg hover:from-purple-600 hover:to-violet-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-purple-500/25 min-w-[70px]"
                                title="Duplicate this entry for next day"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => removeWorkLog(index)}
                                className="px-3 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-medium rounded-lg hover:from-red-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-red-500/25 min-w-[70px]"
                                title="Remove this entry"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m0 0v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Review Section — only on initial submission, not on edit */}
              {selectedJob?.status !== 'Completed' && (
              <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
                <h4 className="text-lg font-semibold text-amber-800 mb-4 flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <span>Leave a Review for the Parent</span>
                </h4>
                
                {/* Rating Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-amber-700 mb-3">
                    Rating
                  </label>
                  <div className="flex justify-center space-x-2">
                    {[...Array(5)].map((_, index) => (
                      <span
                        key={index}
                        className={`text-4xl cursor-pointer transition-all duration-200 transform hover:scale-110 ${
                          index < review.rating ? 'text-yellow-400' : 'text-slate-300'
                        } hover:text-yellow-300`}
                        onClick={() => setReview({ ...review, rating: index + 1 })}
                        title={`${index + 1} star${index === 0 ? '' : 's'}`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-amber-600 text-center mt-2">
                    {review.rating > 0 ? `${review.rating} star${review.rating === 1 ? '' : 's'} selected` : 'Click on a star to rate'}
                  </p>
                </div>

                {/* Comment Section */}
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-2">
                    Comment
                  </label>
                  <textarea
                    value={review.comment}
                    onChange={(e) => setReview({ ...review, comment: e.target.value })}
                    placeholder="Write your review here..."
                    className="w-full p-4 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-500 transition-all duration-200 resize-none"
                    rows="4"
                  ></textarea>
                </div>
              </div>
              )}

              {/* Total Hours Summary */}
              <div className="mb-8 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-emerald-700 font-medium">Total Hours Worked</p>
                      <p className="text-2xl font-bold text-emerald-800">{calculateTotalHours(workLogs)} hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Footer with Action Buttons */}
            <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 border-t border-slate-200/50 rounded-b-2xl flex-shrink-0">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={handleModalClose}
                  className="px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition-all duration-200 border border-slate-200"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Cancel</span>
                  </span>
                </button>
                <button
                  onClick={submitWorkLogs}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transform hover:scale-105 transition-all duration-200 shadow-lg shadow-blue-500/25"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{selectedJob?.status === 'Completed' ? 'Save Changes' : 'Submit Work Logs'}</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {modalType === 'chat' && (
        <Chat applicationId={activeApplicationId} onClose={handleModalClose} />
      )}
  
      <JobTimesModal
        isOpen={isTimesModalOpen}
        onClose={() => setIsTimesModalOpen(false)}
        application={selectedJobForTimes}
      />

      {/* Stripe Onboarding Confirmation Modal */}
      {showOnboardingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Complete Stripe Onboarding</h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Your Stripe account needs to be fully set up before you can accept jobs. 
              This includes completing identity verification and setting up payment methods.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowOnboardingModal(false);
                  setOnboardingLink(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (onboardingLink) {
                    window.open(onboardingLink, "_blank");
                  }
                  setShowOnboardingModal(false);
                  setOnboardingLink(null);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Complete Onboarding
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
  
};

export default SitterJobs;